/**
 * Central analytics event names and property types for PostHog (and GTM).
 * Use these constants for consistent event naming and typed properties.
 */

export const ANALYTICS_EVENTS = {
  // Auth
  SIGN_UP: "sign_up",
  SIGN_UP_ERROR: "sign_up_error",
  SIGNUP_PAGE_VIEWED: "signup_page_viewed",
  SIGNUP_FORM_STARTED: "signup_form_started",
  SIGNUP_SUBMITTED: "signup_submitted",
  /** @deprecated Use SIGNUP_PAGE_VIEWED. Kept so historical queries continue to compile. */
  SIGN_UP_STARTED: "signup_started",
  SIGN_UP_COMPLETED: "signup_completed",
  SIGN_IN: "sign_in",
  SIGN_IN_ERROR: "sign_in_error",
  EMAIL_VERIFIED: "email_verified",
  PASSWORD_RESET_REQUESTED: "password_reset_requested",
  PASSWORD_RESET_COMPLETED: "password_reset_completed",

  // Onboarding
  ONBOARDING_STARTED: "onboarding_started",
  ONBOARDING_STEP_COMPLETED: "onboarding_step_completed",
  ONBOARDING_SKIPPED: "onboarding_skipped",
  ONBOARDING_COMPLETED: "onboarding_completed",
  PROJECT_SETUP_STARTED: "project_setup_started",
  PROJECT_SETUP_SOURCE_SELECTED: "project_setup_source_selected",
  SCREENPLAY_ADDED: "screenplay_added",
  FIRST_VALUE_COMPLETED: "first_value_completed",
  /** A new user with no projects was taken into their demo production once. */
  ONBOARDING_DEMO_LANDING_SHOWN: "onboarding_demo_landing_shown",
  /** "Import your screenplay" clicked on the demo production banner. */
  DEMO_BANNER_IMPORT_CLICKED: "demo_banner_import_clicked",

  // Projects
  PROJECT_CREATED: "project_created",
  PROJECT_OPENED: "project_opened",
  PROJECT_ARCHIVED: "project_archived",
  PROJECT_UNARCHIVED: "project_unarchived",
  PROJECT_DELETED: "project_deleted",
  PROJECT_VERSION_SWITCHED: "project_version_switched",
  PROJECT_CREATE_STARTED: "project_create_started",
  PROJECT_CREATE_SOURCE_SELECTED: "project_create_source_selected",

  // Billing & plans
  PLAN_CLICK: "plan_click",
  PLAN_CLICK_ERROR: "plan_click_error",
  LICENSE_PURCHASE_INITIATED: "license_purchase_initiated",
  LICENSE_PURCHASE_CHECKOUT_REDIRECTED: "license_purchase_checkout_redirected",
  /** Embedded Checkout UI mounted (in-app Studio license purchase). */
  LICENSE_PURCHASE_EMBEDDED_MOUNTED: "license_purchase_embedded_mounted",
  /** Customer finished Embedded Checkout (webhook may still be in flight). */
  LICENSE_PURCHASE_EMBEDDED_COMPLETED: "license_purchase_embedded_completed",
  LICENSE_PURCHASE_FAILED: "license_purchase_failed",
  PROJECT_PURCHASE_SUCCESS: "project_purchase_success",
  WRITER_LICENSE_PURCHASED: "writer_license_purchased",
  STUDIO_LICENSE_PURCHASED: "studio_license_purchased",
  TIER_UPGRADE_PROMPT_SHOWN: "tier_upgrade_prompt_shown",
  SEAT_CAP_REACHED: "seat_cap_reached",
  WRITER_TO_STUDIO_CONVERSION_STARTED: "writer_to_studio_conversion_started",
  WRITER_TO_STUDIO_CONVERSION_COMPLETED: "writer_to_studio_conversion_completed",

  // Product usage (screens & features)
  SCREEN_VIEWED: "screen_viewed",
  SCRIPT_PANEL_VIEWED: "script_panel_viewed",
  /** Right rail / inspector tab (tags, find, details). Never attach screenplay text. */
  SCRIPT_INSPECTOR_TAB_VIEWED: "script_inspector_tab_viewed",
  /** Comments tool sheet opened from the editor chrome. */
  SCRIPT_COMMENTS_TAB_OPENED: "script_comments_tab_opened",
  SCRIPT_COMPARE_SNAPSHOT_OPENED: "script_compare_snapshot_opened",
  /** User completed ending a colored revision pass (API success). */
  SCRIPT_PASS_FINALIZED: "script_pass_finalized",
  /** Post-finalize dialog: keep writing, review tags, or dismiss (no script copy). */
  SCRIPT_FINALIZE_PASS_ACTION: "script_finalize_pass_action",
  SCRIPT_LOCK_DIALOG_OPENED: "script_lock_dialog_opened",
  SCRIPT_DOCUMENT_LOCKED: "script_document_locked",
  SCRIPT_DOCUMENT_UNLOCKED: "script_document_unlocked",
  SCRIPT_DOCUMENT_LOCK_FAILED: "script_document_lock_failed",
  SCRIPT_DOCUMENT_UNLOCK_FAILED: "script_document_unlock_failed",
  SCRIPT_COMMENT_CREATED: "script_comment_created",
  SCRIPT_COMMENT_RESOLVED: "script_comment_resolved",
  SCRIPT_COMMENT_REOPENED: "script_comment_reopened",
  SCRIPT_AI_HUB_OPENED: "script_ai_hub_opened",
  SCRIPT_AI_HUB_CLOSED: "script_ai_hub_closed",
  SCRIPT_AI_HUB_RUN_REQUESTED: "script_ai_hub_run_requested",
  SCRIPT_AI_HUB_RUN_ALL_STARTED: "script_ai_hub_run_all_started",
  SCRIPT_AI_HUB_RUN_FAILED: "script_ai_hub_run_failed",
  AI_RESULT_REVIEWED: "ai_result_reviewed",
  CHARACTERS_PANEL_VIEWED: "characters_panel_viewed",
  SCHEDULE_PANEL_VIEWED: "schedule_panel_viewed",
  BUDGET_PANEL_VIEWED: "budget_panel_viewed",
  STRIPBOARD_VIEWED: "stripboard_viewed",
  /** Successful schedule edit (stripboard / DOOD / shooting calendar). */
  SCHEDULE_UPDATED: "schedule_updated",
  /** Successful budget edit (line item / assumptions / structure). */
  BUDGET_UPDATED: "budget_updated",
  ACTOR_CREATED: "actor_created",
  ACTOR_UPDATED: "actor_updated",
  ACTOR_DELETED: "actor_deleted",
  CHARACTER_CREATED: "character_created",
  CHARACTER_UPDATED: "character_updated",
  CHARACTER_LINKED: "character_linked",
  EXPORT_STARTED: "export_started",
  EXPORT_COMPLETED: "export_completed",
  EXPORT_FAILED: "export_failed",

  // Product discovery
  NAVIGATION_SEARCH_OPENED: "navigation_search_opened",
  NAVIGATION_SEARCH_RESULT_SELECTED: "navigation_search_result_selected",
  /**
   * A navigation control was used: a sidebar link (step item, project link,
   * settings section), a header breadcrumb switcher, or an empty state's
   * Generate button (which only opens the Run AI dialog, never a paid run).
   */
  NAV_ITEM_CLICKED: "nav_item_clicked",
  /** The project name in the header breadcrumb opened the project switcher. */
  PROJECT_SWITCHER_OPENED: "project_switcher_opened",
  /** Another project was picked in the project switcher. */
  PROJECT_SWITCHED: "project_switched",

  // Workspace
  WORKSPACE_SETTINGS_VIEWED: "workspace_settings_viewed",
  WORKSPACE_MEMBER_INVITED: "workspace_member_invited",
  WORKSPACE_MEMBER_ROLE_CHANGED: "workspace_member_role_changed",
  WORKSPACE_MEMBER_REMOVED: "workspace_member_removed",
  WORKSPACE_INVITATION_CANCELLED: "workspace_invitation_cancelled",

  // User & settings
  SETTINGS_PAGE_VIEWED: "settings_page_viewed",
  PROFILE_UPDATED: "profile_updated",
  MFA_ENABLED: "mfa_enabled",
  MFA_DISABLED: "mfa_disabled",
  BILLING_PORTAL_OPENED: "billing_portal_opened",
  NOTIFICATION_PREFERENCES_UPDATED: "notification_preferences_updated",
  APPEARANCE_PREFERENCES_CHANGED: "appearance_preferences_changed",

  // Create project flow
  CREATE_PROJECT_SHEET_OPENED: "create_project_sheet_opened",
  CREATE_PROJECT_SHEET_CLOSED: "create_project_sheet_closed",
  /** Parse API or client validation failed (safe metadata only — no screenplay text). */
  SCREENPLAY_PARSE_FAILED: "screenplay_parse_failed",
  /** react-dropzone rejected file before parse. */
  SCREENPLAY_DROP_REJECTED: "screenplay_drop_rejected",
  /** Create/version API or network failed (not user cancel). */
  PROJECT_CREATE_FAILED: "project_create_failed",
  /** Create aborted while pending (sheet dismiss, gate close, unmount, canceled XHR). */
  PROJECT_CREATE_ABORTED: "project_create_aborted",
  /** User dismissed the create surface while the create continued in the background. */
  PROJECT_CREATE_BACKGROUNDED: "project_create_backgrounded",
  /** A screenplay file was accepted and parsed into the sheet (metadata only — no file name). */
  SCREENPLAY_FILE_ATTACHED: "screenplay_file_attached",
  /** Parse request sent to the API. */
  SCREENPLAY_PARSE_STARTED: "screenplay_parse_started",
  /** Parse returned usable scenes. */
  SCREENPLAY_PARSE_SUCCEEDED: "screenplay_parse_succeeded",
  /** Submit clicked but schema validation stopped it (field names only — never values). */
  PROJECT_CREATE_VALIDATION_BLOCKED: "project_create_validation_blocked",
  /** Submit reached the handler but a guard refused it (no file, no permission, task lock). */
  PROJECT_CREATE_BLOCKED: "project_create_blocked",
  /** A new version was created on an existing project. Kept out of the create funnel. */
  PROJECT_VERSION_CREATED: "project_version_created",

  // App error recovery (metadata only — never include user content)
  APP_ERROR_SHOWN: "app_error_shown",
  APP_ERROR_RETRY_CLICKED: "app_error_retry_clicked",
  APP_ERROR_RECOVERED: "app_error_recovered",

  // Feedback funnel (metadata only — never include feedback text or PII)
  FEEDBACK_OPENED: "feedback_opened",
  FEEDBACK_CATEGORY_SELECTED: "feedback_category_selected",
  FEEDBACK_VALIDATION_BLOCKED: "feedback_validation_blocked",
  FEEDBACK_SUBMIT_STARTED: "feedback_submit_started",
  FEEDBACK_SUBMITTED: "feedback_submitted",
  FEEDBACK_RATE_LIMITED: "feedback_rate_limited",
  FEEDBACK_SUBMIT_FAILED: "feedback_submit_failed",

  CONNECTOR_KEY_CREATED: "connector_key_created",
} as const;

export const ANALYTICS_SCHEMA_VERSION = 2 as const;

export type FirstValueType =
  | "script_breakdown_reviewed"
  | "coverage_reviewed"
  | "schedule_reviewed"
  | "schedule_edited"
  | "budget_reviewed"
  | "budget_edited";

export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

export interface SignUpProperties {
  userId?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  sign_up_method?: string;
}

export interface SignInProperties {
  userId?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  sign_up_method?: string;
  is_mfa_enabled?: boolean;
  is_verified?: boolean;
  is_active?: boolean;
  subscription_type?: string;
}

export interface ProjectCreatedProperties {
  project_id: string;
  is_trial?: boolean;
  production_type?: string;
  workspace_id?: string;
  create_source?: "blank" | "import";
}

export interface ProjectOpenedProperties {
  project_id: string;
  workspace_id: string;
  project_name?: string;
  from?: "list" | "dropdown" | "recent";
}

/**
 * Where a navigation click happened: a sidebar area (one per app context),
 * the app header (breadcrumb switchers), or a step page's empty state.
 */
export type NavArea =
  | "project"
  | "projects_home"
  | "workspace_settings"
  | "personal_settings"
  | "footer"
  | "header"
  | "empty_state";

export type NavDevice = "desktop" | "mobile";

export type NavStep = "script" | "characters" | "schedule" | "budget";

export interface NavItemClickedProperties {
  area: NavArea;
  /** Production step that owns the item, when the item is a step page. */
  step?: NavStep;
  /** Stable item id from the navigation config, e.g. `budget-fringes`. */
  item: string;
  collapsed: boolean;
  device: NavDevice;
  workspace_id?: string;
  project_id?: string;
}

export interface ProjectSwitcherOpenedProperties {
  project_id: string;
  workspace_id: string;
  collapsed: boolean;
  device: NavDevice;
}

export interface ProjectSwitchedProperties {
  from_project_id: string;
  to_project_id: string;
  workspace_id: string;
  /** `search` when the list was filtered, `recent` otherwise. */
  via: "recent" | "search";
}

export interface ProjectVersionSwitchedProperties {
  project_id: string;
  document_id: string;
  version_name?: string;
  source: "sidebar_switcher";
}

export interface ScreenViewedProperties {
  screen_name: string;
  screen_path: string;
  workspace_id?: string;
  project_id?: string;
  section?: string;
}

export interface PlanClickProperties {
  plan_id?: string;
  plan_name?: string;
  source?: string;
}

export interface WorkspaceGroupProperties {
  name?: string;
  plan?: string;
  member_count?: number;
}

export type AppearancePreferenceKey = "source" | "mode" | "theme" | "font" | "font_size";

export interface AppearancePreferencesChangedProperties {
  preference: AppearancePreferenceKey;
  value: string;
  previous_value: string;
  source: string;
  mode: string;
  theme: string;
  font: string;
  font_size: string;
  resolved_mode: string;
  resolved_theme: string;
  solar_phase?: string;
}

export type AnalyticsEventProperties =
  | SignUpProperties
  | SignInProperties
  | ProjectCreatedProperties
  | ProjectOpenedProperties
  | NavItemClickedProperties
  | ProjectSwitcherOpenedProperties
  | ProjectSwitchedProperties
  | ProjectVersionSwitchedProperties
  | ScreenViewedProperties
  | PlanClickProperties
  | WorkspaceGroupProperties
  | Record<string, string | number | boolean | undefined>;
